import { useCallback, useEffect, useState } from "react";
import { Button, toaster } from "rsuite";
import { ElectronicClockApi } from "../../../../../services/ElectronicClock.api";
import NotificationFactory from "../../../../../shared/Notifications";
import { SharedApi } from "../../../../../services/Shared.api";
import DevicesModal from "../../../../modals/DeviceModal/DeviceModal";
import { DeviceListItem, DevicesResponse } from "../../../../modals/modals.model";
import "./ElectronicClock.scss";

type ElectronicClockProps = {
  deviceId: string;
};

const ElectronicClock = ({ deviceId }: ElectronicClockProps) => {
  const [loading, setLoading] = useState(false);
  const [isMeteoModalOpen, setIsMeteoModalOpen] = useState(false);
  const [meteoSourceDeviceId, setMeteoSourceDeviceId] = useState<string | null>(null);
  const [meteoSourceLabel, setMeteoSourceLabel] = useState("По координатам");

  const formatMeteoSourceLabel = useCallback((device: DeviceListItem) => {
    if (device.kind === "coords") {
      return "По координатам";
    }

    const title = device.kind === "clock" ? "Часовой механизм" : "Блок датчиков";
    const model = device.model ? ` ${device.model}` : "";
    const address = device.address ? ` | ${device.address}` : "";

    return `${title}${model}${address}`;
  }, []);

  const resolveMeteoSourceLabel = useCallback(async (selectedDeviceId: string | null) => {
    if (!selectedDeviceId) {
      setMeteoSourceLabel("По координатам");
      return;
    }

    try {
      const response = (await SharedApi.getDevices(String(deviceId))) as DevicesResponse;
      const { clocks = [], sensors = [] } = response || {};
      const devices: DeviceListItem[] = [
        ...clocks.map((item) => ({ ...item, kind: "clock" as const })),
        ...sensors.map((item) => ({ ...item, kind: "sensor" as const })),
      ];
      const selectedDevice = devices.find((item) => String(item.id) === selectedDeviceId);

      setMeteoSourceLabel(
        selectedDevice ? formatMeteoSourceLabel(selectedDevice) : `Устройство ${selectedDeviceId}`
      );
    } catch (error) {
      console.error("Ошибка получения списка метео-устройств:", error);
      setMeteoSourceLabel(`Устройство ${selectedDeviceId}`);
    }
  }, [deviceId, formatMeteoSourceLabel]);

  useEffect(() => {
    if (deviceId) {
      const loadMeteoSource = async () => {
        setLoading(true);
        try {
          const response = await ElectronicClockApi.getMeteoSource(String(deviceId));
          const selectedDeviceId = response.deviceId ?? null;

          setMeteoSourceDeviceId(selectedDeviceId);
          await resolveMeteoSourceLabel(selectedDeviceId);
        } catch (error) {
          console.error("Ошибка получения источника метеоданных:", error);
          toaster.push(NotificationFactory.error(), { placement: "topEnd" });
        } finally {
          setLoading(false);
        }
      };

      loadMeteoSource().then();
    }
  }, [deviceId, resolveMeteoSourceLabel]);

  const handleSelectMeteoDevice = async (device: DeviceListItem) => {
    if (!deviceId) return;

    setLoading(true);
    try {
      const selectedDeviceId = device.id === "coordinates" ? null : String(device.id);
      const result = await ElectronicClockApi.updateMeteoSource(String(deviceId), selectedDeviceId);

      if (result) {
        setMeteoSourceDeviceId(selectedDeviceId);
        setMeteoSourceLabel(formatMeteoSourceLabel(device));
        toaster.push(NotificationFactory.success(), { placement: "topEnd" });
      }
    } catch (error) {
      console.error("Ошибка обновления источника метеоданных:", error);
      toaster.push(NotificationFactory.error(), { placement: "topEnd" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fieldset-wrapper">
        <div className="legend-title main-legend-title">Электронные часы</div>
        <div className="row p-2 mt-1 mb-2 pb-0">
          <div className="col-xxl-4 col-xl-6 col-lg-12 col-md-12 electronic-clock__meteo-source">
            <span className="electronic-clock__meteo-source-label">Метеоданные:</span>
            <Button
              size="xs"
              appearance="subtle"
              disabled={loading || !deviceId}
              title={meteoSourceDeviceId ? `ID: ${meteoSourceDeviceId}` : "По координатам"}
              onClick={() => setIsMeteoModalOpen(true)}
            >
              {meteoSourceLabel}
            </Button>
          </div>
        </div>
      </div>
      <DevicesModal
        open={isMeteoModalOpen}
        deviceId={deviceId}
        onClose={() => setIsMeteoModalOpen(false)}
        onSelectDevice={handleSelectMeteoDevice}
      />
    </>
  );
};

export default ElectronicClock;
